import { useAuth } from "@clerk/clerk-react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Skeleton,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { NewCampaignContext } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import { CampaignStatus } from "schemas/dashboard";
import { CustomEvent, Map } from "schemas/functions";

import StyledSelect from "components/Styled/Select";
import StyledTextField from "components/Styled/TextField";
import TimezoneAutocomplete from "components/TimezoneAutocomplete";
import { labelSx } from "features/Influencer/Autopilot/styles";
import BrandCard from "features/Influencer/Discover/Results/BrandCard";
import { BrandCardSource } from "features/Influencer/Discover/Results/schema";
import GoogleConnect from "features/Settings/GoogleConnect";
import { fetcherAuth } from "utils/api";
import { addBusinessDays } from "utils/time";

import PreviewCampaignBrands from "../../../NewCampaign/SharedComponents/PreviewCampaignBrands";
import StepNav from "../../StepNav";
import styles from "./styles";

const EMAILS_PER_DAY_OPTIONS = [5, 10, 20, 25, 30];
const DEFAULT_EMAILS_PER_DAY = 20;
const DEFAULT_TIMEZONE = "America/New_York";

interface PreviewTemplate {
  id: number;
  name: string;
  contentType: string;
  previewSubject?: string;
  previewBody: string;
}
interface PreviewData {
  previewTemplates: PreviewTemplate[];
  previewBrandName: string;
  brandsCount: number;
}

interface Props {
  handleUpdateCampaign: (body: Map, skipMoveNext?: boolean) => void;
  moveBack: () => void;
  onEditBrands: () => void;
  onEditTemplates: () => void;
  submitLoading?: boolean;
  errorMessage?: string;
}

export default function Review({
  handleUpdateCampaign,
  moveBack,
  onEditBrands,
  onEditTemplates,
  submitLoading = false,
  errorMessage = "",
}: Props) {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { campaign } = useContext(NewCampaignContext);
  const { emailHealth, fetchIntegrationHealth } = useContext(
    UserIntegrationsContext,
  );

  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [brandsDialogOpen, setBrandsDialogOpen] = useState(false);

  const [draftName, setDraftName] = useState(campaign?.name ?? "");
  const [draftStartDate, setDraftStartDate] = useState(
    campaign?.startDate ?? "",
  );

  const minStartDate = addBusinessDays(1).toISOString().split("T")[0];
  const isDraft = campaign?.status === CampaignStatus.draft;

  useEffect(() => {
    setDraftName(campaign?.name ?? "");
    setDraftStartDate(campaign?.startDate ?? "");
  }, [campaign?.name, campaign?.startDate]);

  const fetchPreview = async () => {
    if (!currentOrg?.id || !campaign?.id) return;
    setLoading(true);
    try {
      const { campaign: data } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/campaigns/${campaign.id}/draft/preview`,
      );
      setPreviewData({
        previewTemplates: data.previewTemplates,
        previewBrandName: data.previewBrandName,
        brandsCount: data.brandsCount,
      });
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (campaign?.id && currentOrg?.id) {
      fetchPreview();
      if (!emailHealth) fetchIntegrationHealth(currentOrg?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.id, currentOrg?.id, emailHealth]);

  const saveDetail = (body: Map) => handleUpdateCampaign(body, true);

  const emailsPerDayWarning = (() => {
    if (
      !campaign?.emailsPerDay ||
      !campaign?.startDate ||
      !campaign?.endDate ||
      !previewData?.brandsCount
    )
      return null;
    const start = new Date(campaign.startDate);
    const end = new Date(campaign.endDate);
    const days = Math.max(
      1,
      Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1,
    );
    const requiredPerDay = previewData.brandsCount / days;
    if (requiredPerDay > campaign.emailsPerDay) {
      return `${previewData.brandsCount} emails over ${days} day${days !== 1 ? "s" : ""} needs ~${Math.ceil(requiredPerDay)}/day, but you're sending ${campaign.emailsPerDay}/day. Consider fewer brands or a higher daily limit.`;
    }
    return null;
  })();

  const canLaunch =
    campaign?.status === CampaignStatus.draft ||
    campaign?.status === CampaignStatus.pending_approval;

  const template = previewData?.previewTemplates?.[activeIndex];
  const followupDelayDays =
    activeIndex > 0
      ? campaign?.emailSetting?.emailSettingTemplates?.find(
          (est) => est.emailOrder === activeIndex,
        )?.delayDays
      : null;

  const brandCount = previewData?.brandsCount ?? campaign?.campaignBrandCount;
  const previewBrands = campaign?.campaignBrands?.slice(0, 3) ?? [];

  const editButton = (onClick: () => void) =>
    isDraft ? (
      <Button size="small" onClick={onClick} sx={styles.editButton}>
        <Box
          component="i"
          className="fa-regular fa-pen"
          sx={{ mr: 0.5, fontSize: 12 }}
        />
        Edit
      </Button>
    ) : null;

  return (
    <Box sx={styles.page}>
      {(errorMessage || !emailHealth) && (
        <Alert
          severity={errorMessage ? "error" : "warning"}
          sx={{ mb: 2, borderRadius: "12px" }}
          action={!emailHealth ? <GoogleConnect /> : undefined}
        >
          {errorMessage ||
            "Your Google integration isn't connected. Reconnect to launch."}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={styles.section}>
              <Skeleton
                variant="text"
                width={120}
                sx={{ fontSize: 15, mb: 1.5 }}
              />
              <Skeleton variant="rounded" height={i === 2 ? 120 : 64} />
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Details — shown as the same fields you edit */}
          <Box sx={styles.section}>
            <Typography sx={{ ...styles.sectionTitle, mb: 2 }}>
              Details
            </Typography>
            <Box sx={styles.detailFields}>
              <Box>
                <Typography sx={{ ...labelSx, mb: 0.75 }}>Name</Typography>
                <StyledTextField
                  fullWidth
                  value={draftName}
                  disabled={!isDraft}
                  onChange={(e: CustomEvent) => setDraftName(e.target.value)}
                  onBlur={() => {
                    const next = draftName.trim();
                    if (next && next !== campaign?.name)
                      saveDetail({ name: next });
                  }}
                />
              </Box>
              <Box sx={styles.detailRow}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ ...labelSx, mb: 0.75 }}>
                    Start date
                  </Typography>
                  <StyledTextField
                    type="date"
                    fullWidth
                    value={draftStartDate}
                    disabled={!isDraft}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: minStartDate }}
                    onChange={(e: CustomEvent) => {
                      setDraftStartDate(e.target.value);
                      if (e.target.value)
                        saveDetail({ startDate: e.target.value });
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ ...labelSx, mb: 0.75 }}>
                    Emails per day
                  </Typography>
                  <FormControl fullWidth>
                    <StyledSelect
                      value={campaign?.emailsPerDay ?? DEFAULT_EMAILS_PER_DAY}
                      disabled={!isDraft}
                      onChange={(e: CustomEvent) =>
                        saveDetail({ emailsPerDay: e.target.value as number })
                      }
                    >
                      {EMAILS_PER_DAY_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n} / day
                          {n === DEFAULT_EMAILS_PER_DAY && (
                            <Chip
                              label="Recommended"
                              size="small"
                              sx={styles.recommendedChip}
                            />
                          )}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ ...labelSx, mb: 0.75 }}>
                    Timezone
                  </Typography>
                  <FormControl fullWidth>
                    <TimezoneAutocomplete
                      value={campaign?.timezone ?? DEFAULT_TIMEZONE}
                      disabled={!isDraft}
                      onChange={(_, v) =>
                        saveDetail({ timezone: v || DEFAULT_TIMEZONE })
                      }
                    />
                  </FormControl>
                </Box>
              </Box>
            </Box>
            {emailsPerDayWarning && isDraft && (
              <Alert severity="warning" sx={{ mt: 1.5, fontSize: 13 }}>
                {emailsPerDayWarning}
              </Alert>
            )}
          </Box>

          {/* Brands */}
          <Box sx={styles.section}>
            <Box sx={styles.sectionHeader}>
              <Typography sx={styles.sectionTitle}>
                Brands{brandCount != null ? ` (${brandCount})` : ""}
              </Typography>
              {editButton(onEditBrands)}
            </Box>
            {previewBrands.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {previewBrands.map((cb) => (
                  <BrandCard
                    key={cb.bentoBrand?.id}
                    brand={cb.bentoBrand}
                    source={BrandCardSource.CAMPAIGN_BRAND}
                  />
                ))}
                {(brandCount ?? 0) > previewBrands.length && (
                  <Button
                    onClick={() => setBrandsDialogOpen(true)}
                    sx={{ ...styles.linkButton, alignSelf: "flex-start" }}
                  >
                    View all {brandCount} brands
                  </Button>
                )}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                No brands selected yet.
              </Typography>
            )}
          </Box>

          {/* Emails */}
          <Box sx={styles.section}>
            <Box sx={styles.sectionHeader}>
              <Typography sx={styles.sectionTitle}>Emails</Typography>
              {editButton(onEditTemplates)}
            </Box>

            {previewData &&
            previewData.previewTemplates.length > 0 &&
            template ? (
              <>
                {previewData.previewTemplates.length > 1 && (
                  <Box sx={styles.emailTabs}>
                    {previewData.previewTemplates.map((_, i) => (
                      <Box
                        key={i}
                        onClick={() => setActiveIndex(i)}
                        sx={styles.emailTab(i === activeIndex)}
                      >
                        {i === 0 ? "Outreach" : `Follow-up ${i}`}
                      </Box>
                    ))}
                  </Box>
                )}
                <Box sx={styles.compose}>
                  <Box sx={styles.composeBar}>
                    <Box
                      component="i"
                      className="fa-regular fa-pen-to-square"
                      sx={{ fontSize: 12, color: grey[500] }}
                    />
                    <Typography sx={{ fontSize: 12, color: grey[500] }}>
                      Preview
                    </Typography>
                    {activeIndex > 0 && followupDelayDays != null && (
                      <Chip
                        size="small"
                        label={`Sent ${followupDelayDays} business day${followupDelayDays === 1 ? "" : "s"} later`}
                        sx={styles.followupChip}
                      />
                    )}
                  </Box>
                  <Box sx={styles.composeRow}>
                    <Typography sx={styles.composeLabel}>To</Typography>
                    <Typography sx={styles.composeValue}>
                      {previewData.previewBrandName}
                    </Typography>
                  </Box>
                  <Box sx={styles.composeRow}>
                    <Typography sx={styles.composeLabel}>Subject</Typography>
                    <Typography
                      sx={{ ...styles.composeValue, fontWeight: 600 }}
                    >
                      {template.previewSubject || "—"}
                    </Typography>
                  </Box>
                  <Box
                    sx={styles.composeBody}
                    dangerouslySetInnerHTML={{ __html: template.previewBody }}
                  />
                </Box>
              </>
            ) : (
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                No email template selected yet.
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Dialog
        open={brandsDialogOpen}
        onClose={() => setBrandsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={styles.dialogTitle}>
          Selected brands
          <IconButton size="small" onClick={() => setBrandsDialogOpen(false)}>
            <Box component="i" className="fa-regular fa-xmark" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <PreviewCampaignBrands />
        </DialogContent>
      </Dialog>

      <StepNav
        onBack={moveBack}
        onNext={
          canLaunch
            ? () => handleUpdateCampaign({ status: CampaignStatus.in_progress })
            : undefined
        }
        nextLabel="Launch Campaign"
        nextIcon="fa-solid fa-stars"
        nextDisabled={!canLaunch || !emailHealth}
        nextLoading={submitLoading}
      />
    </Box>
  );
}
